import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Search, Sparkles } from 'lucide-react'
import Button from '../ui/Button'

interface MarketViewSearchProps {
  onSearch: (query: string) => void
  isLoading?: boolean
  initialQuery?: string
}

const exampleQueries = [
  'Fixed wing tactical UAS drones',
  'Counter-UAS systems with electronic warfare capability',
  'Autonomous ground vehicles for logistics',
  'Portable satellite communication terminals',
  'Night vision systems with thermal fusion',
]

const MarketViewSearch: React.FC<MarketViewSearchProps> = ({
  onSearch,
  isLoading = false,
  initialQuery = '',
}) => {
  const [query, setQuery] = useState(initialQuery)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const autoResize = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = 'auto'
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 24
    const maxHeight = lineHeight * 4
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`
  }, [])

  useEffect(() => {
    autoResize()
  }, [query, autoResize])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      onSearch(query.trim())
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (query.trim()) {
        onSearch(query.trim())
      }
    }
  }

  const handleExampleClick = (example: string) => {
    setQuery(example)
    onSearch(example)
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh] px-3 sm:px-4 py-6 sm:py-8">
      {/* Logo / Title */}
      <div className="mb-6 sm:mb-8 text-center">
        <h1 className="text-3xl sm:text-4xl font-semibold text-foreground mb-2 tracking-tight">
          MarketView
        </h1>
        <p className="text-muted-foreground text-base sm:text-lg">
          Explore defense technology products and vendors
        </p>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSubmit} className="w-full max-w-2xl">
        <div className="relative">
          <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
          <textarea
            ref={textareaRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe what you're looking for..."
            className="w-full pl-10 sm:pl-12 pr-24 sm:pr-32 py-3 sm:py-3.5 text-base sm:text-lg rounded-xl border-2 border-border focus:border-primary shadow-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none resize-none overflow-hidden"
            disabled={isLoading}
            autoFocus
            rows={1}
          />
          <Button
            type="submit"
            disabled={!query.trim() || isLoading}
            className="absolute right-1.5 sm:right-2 top-1/2 -translate-y-1/2 px-3 sm:px-6 text-sm"
          >
            {isLoading ? (
              <span className="flex items-center gap-1.5 sm:gap-2">
                <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-pulse" />
                <span className="hidden sm:inline">Searching...</span>
              </span>
            ) : (
              'Search'
            )}
          </Button>
        </div>
      </form>

      {/* Example Queries */}
      <div className="mt-6 sm:mt-8 text-center max-w-2xl">
        <p className="text-sm text-muted-foreground mb-3">Try an example:</p>
        <div className="flex flex-wrap justify-center gap-2">
          {exampleQueries.map((example, index) => (
            <button
              key={index}
              onClick={() => handleExampleClick(example)}
              className="px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm bg-card border border-border rounded-full 
                         text-muted-foreground hover:text-foreground hover:border-primary/50
                         transition-colors duration-200 max-w-full truncate"
              title={example}
            >
              {example.length > 40 ? example.slice(0, 40) + '...' : example}
            </button>
          ))}
        </div>
      </div>

      {/* AI Badge */}
      <div className="mt-8 sm:mt-12 flex items-center gap-2 text-muted-foreground text-xs sm:text-sm">
        <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
        <span>Powered by semantic search across {'>'}1000 products</span>
      </div>
    </div>
  )
}

export default MarketViewSearch
